/**
 * globalSearch — single source of truth for the ⌘K palette's cross-resource
 * search ("search everything, everywhere").
 *
 * Each content type contributes a "source": either an async API search (the
 * large datasets — Sessions, Quick Taps) or a sync filter over data already in
 * the Redux store (the smaller reference collections). The palette runs ALL of
 * them on every query and groups the results, so search works from ANY page —
 * including the Dashboard — not just the current section.
 *
 * Loading: the per-page lists are fetched lazily when their page mounts, so a
 * cold store (e.g. straight to the Dashboard) wouldn't have them. `ensureContentLoaded`
 * dispatches the existing `fetchAll*` actions for any empty slice when the
 * palette opens, so the small collections are warm by the time the user types.
 *
 * NOTE: these mappings mirror the per-page `usePaletteRecords` adapters. If a
 * page changes how it labels/links a resource, update it here too. (A future
 * cleanup could have the pages import their source from here to dedupe.)
 *
 * Cards are intentionally excluded — they're deck-scoped (only the open deck's
 * cards are in the store), so they can't be searched globally. Card Decks are
 * included.
 */
import { format } from 'date-fns';
import { apiV2 } from '../../actions';
import { fetchAllCategories } from '../../actions/categoryActions';
import { fetchAllSubCategories } from '../../actions/subcategoryActions';
import { fetchAuthors } from '../../actions/authorsActions';
import { fetchAllChallenges } from '../../actions/challengeActions';
import { fetchAllSeries } from '../../actions/seriesActions';
import { fetchAllDailyReflections } from '../../actions/dailyReflectionActions';
import { fetchAllBGM } from '../../actions/backgroundMusicActions';
import { fetchTappingPoints } from '../../actions/tappingPointsActions';
import { fetchAllPromoBanners } from '../../actions/promoBannerActions';
import { fetchAllQuotes } from '../../actions/quoteActions';
import { fetchAllDecks } from '../../actions/cardDeckActions';
import { fetchAllMarketingInitiatives } from '../../actions/marketingInitiativeActions';
import { fetchAllMediaResources } from '../../actions/mediaResourceActions';

/* ─── helpers ─────────────────────────────────────────────────────────── */

/**
 * Page size for the API-backed sources.
 *
 * This module deliberately does NOT cap what it returns — every match a source
 * finds comes back, and the palette decides how many to render at a time (see
 * INITIAL_PER_GROUP / "Show more" in CommandPalette). Truncating here is what
 * caused the original bug: a hard cap of 6 meant searching "binaural" returned
 * six of eleven sessions and the other five could not be reached at all.
 *
 * So this constant is the one real ceiling left, and only for the API sources —
 * the Redux-backed ones are filtered from data already in the store and are
 * never truncated. 100 is well inside the endpoint's `max:200` validation.
 */
const API_SEARCH_PER_PAGE = 100;

const inc = (value, q) => (value || '').toLowerCase().includes(q);

/** Redux slices are sometimes arrays, sometimes id-keyed maps — normalize. */
const asArray = (v) => (Array.isArray(v) ? v : v ? Object.values(v) : []);

function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '';
  const n = Math.max(0, Math.round(Number(seconds)));
  const min = Math.floor(n / 60);
  const sec = n % 60;
  return `${min}:${sec < 10 ? '0' : ''}${sec}`;
}

// Mirrors SessionsTableV2's resolveParent (array-shaped relations).
//
// `day` is a human-readable label the API supplies for the two parent types
// that have per-day sessions — series (APIv2 #744) and challenges (#743). Their
// per-day sessions routinely share a title verbatim, so it is the only thing
// that tells them apart in a list. The API normalises both to the same "Day 4"
// string shape, so nothing here needs to know which parent it came from.
//
// Optional on purpose: against an API predating those PRs the field is simply
// absent and the meta reads exactly as it did before.
function sessionParent(s) {
  const cat = s.categories?.[0];
  if (cat) return { type: 'Category', name: cat.title };
  const sub = s.subcategories?.[0];
  if (sub) return sub.is_book
    ? { type: 'Audiobook', name: sub.title }
    : { type: 'Subcategory', name: sub.title };
  const ser = s.series?.[0];
  if (ser) return { type: 'Series', name: ser.title, day: ser.day || null };
  const cha = s.challenges?.[0];
  if (cha) return { type: 'Challenge', name: cha.title, day: cha.day || null };
  return { type: '', name: null };
}

/** "Challenge · 90 Day Pain Reprocessing Protocol · Day 64", skipping absent parts. */
function sessionMeta(parent) {
  if (!parent.name) return 'Standalone';
  return [parent.type, parent.name, parent.day].filter(Boolean).join(' · ');
}

// Mirrors QuickTapsTableV2's resolveParent (single-object relations).
function quickTapParent(qt) {
  if (qt.subcategory) return { type: 'Subcategory', name: qt.subcategory.subcategory_title };
  if (qt.category) return { type: 'Category', name: qt.category.category_title };
  return { type: '', name: null };
}

async function apiSearch(endpoint, q, signal) {
  const res = await apiV2.get(endpoint, {
    params: { q, per_page: API_SEARCH_PER_PAGE, sort: 'created_at', dir: 'desc' },
    signal,
  });
  return res?.data?.data || [];
}

/* ─── sources ─────────────────────────────────────────────────────────── */

/**
 * Each source: { key, label, icon, route, async?, fetch?(q,signal), records?(state,q) }
 * `route` is the resource's list-page path, used to float the current section first.
 */
export const CONTENT_SOURCES = [
  {
    key: 'sessions', label: 'Sessions', icon: 'sessions', route: '/sessions', async: true,
    // One row per session. Sessions legitimately repeat a title — days 64-70 of
    // the "90 Day Pain Reprocessing Protocol" are seven separate, separately
    // editable sessions all named "Deep Sleep Patterning with Binaural Beats" —
    // and every one of them has to stay individually openable, so they are NOT
    // de-duplicated or collapsed here.
    fetch: async (q, signal) => (await apiSearch('/sessions', q, signal)).map((s) => ({
      label: s.session_name,
      meta: sessionMeta(sessionParent(s)),
      path: `/sessions/edit/${s.session_id}`,
    })),
  },
  {
    key: 'quick_taps', label: 'Quick Taps', icon: 'quick_taps', route: '/quick-taps', async: true,
    fetch: async (q, signal) => (await apiSearch('/quick-tap-sessions', q, signal)).map((qt) => {
      const p = quickTapParent(qt);
      return { label: qt.title, meta: p.name ? `${p.type} · ${p.name}` : 'No parent', path: `/quick-taps/edit/${qt.id}` };
    }),
  },
  {
    key: 'categories', label: 'Categories', icon: 'categories', route: '/category',
    records: (s, q) => asArray(s.categories?.all_categories)
      .filter((c) => inc(c.category_title, q))
      .map((c) => ({ label: c.category_title || 'Untitled', meta: c.category_is_quick_tap ? 'Quick Tap' : 'Audio', path: `/category/edit/${c.category_id}` })),
  },
  {
    key: 'subcategories', label: 'Subcategories', icon: 'subcategory', route: '/subcategories',
    records: (s, q) => asArray(s.subcategories?.subcategories)
      .filter((x) => !x.subcategory_is_book && inc(x.subcategory_title, q))
      .map((x) => ({
        label: x.subcategory_title || 'Untitled',
        meta: x.category_title
          ? `${x.subcategory_is_quick_tap ? 'Quick Tap' : 'Audio'} · ${x.category_title}`
          : (x.subcategory_is_quick_tap ? 'Quick Tap' : 'Audio'),
        path: `/subcategories/edit/${x.subcategory_id}`,
      })),
  },
  {
    key: 'audiobooks', label: 'Audiobooks', icon: 'audiobooks', route: '/audiobooks',
    records: (s, q) => asArray(s.subcategories?.subcategories)
      .filter((x) => x.subcategory_is_book && inc(x.subcategory_title, q))
      .map((x) => ({ label: x.subcategory_title || 'Untitled', meta: x.subcategory_is_active ? 'Active' : 'Inactive', path: `/audiobooks/edit/${x.subcategory_id}` })),
  },
  {
    key: 'authors', label: 'Authors', icon: 'authors', route: '/authors',
    records: (s, q) => asArray(s.authors?.authors)
      .filter((a) => inc(a.author_name, q))
      .map((a) => ({ label: a.author_name || 'Untitled', meta: a.author_title || '', path: `/authors/edit/${a.author_id}` })),
  },
  {
    key: 'challenges', label: 'Challenges', icon: 'challenges', route: '/challenges',
    records: (s, q) => asArray(s.challenges?.challenges)
      .filter((c) => inc(c.challenge_title, q))
      .map((c) => ({ label: c.challenge_title || 'Untitled', meta: Number(c.challenge_is_active) === 1 ? 'Active' : 'Inactive', path: `/challenges/edit/${c.challenge_id}` })),
  },
  {
    key: 'series', label: 'Series', icon: 'series', route: '/series',
    records: (s, q) => asArray(s.series?.all_series)
      .filter((x) => inc(x.series_title, q))
      .map((x) => ({ label: x.series_title || 'Untitled', meta: x.category_title || (x.series_is_active ? 'Active' : 'Inactive'), path: `/series/edit/${x.series_id}` })),
  },
  {
    key: 'daily-inspirations', label: 'Daily Inspirations', icon: 'daily-inspirations', route: '/daily-inspirations',
    records: (s, q) => asArray(s.daily_reflections?.daily_reflections)
      .filter((it) => inc(it.title, q))
      .map((it) => ({ label: it.title || 'Untitled', meta: it.date_epoch ? format(new Date(it.date_epoch * 1000), 'MMM d, yyyy') : '', path: `/daily-inspirations/${it.daily_reflection_id}/edit` })),
  },
  {
    key: 'background-music', label: 'Background Music', icon: 'background-music', route: '/background-musics',
    records: (s, q) => asArray(s.background_musics?.background_musics)
      .filter((b) => inc(b.bgm_title, q))
      .map((b) => ({ label: b.bgm_title || 'Untitled', meta: formatDuration(b.bgm_audio_length), path: `/background-musics/edit/${b.bgm_id}` })),
  },
  {
    key: 'tapping-points', label: 'Tapping Points', icon: 'tapping-points', route: '/tapping-points',
    records: (s, q) => asArray(s.tapping_points?.tapping_points)
      .filter((tp) => inc(tp.tapping_point_name, q))
      .map((tp) => ({ label: tp.tapping_point_name || 'Untitled', meta: tp.tapping_point_is_default ? 'Default' : '', path: `/tapping-points/edit/${tp.tapping_point_id}` })),
  },
  {
    key: 'banners', label: 'Upgrade Banners', icon: 'banners', route: '/banners',
    records: (s, q) => asArray(s.banners?.banners)
      .filter((b) => inc(b.promo_banner_title, q))
      .map((b) => ({ label: b.promo_banner_title || 'Untitled', meta: b.promo_banner_is_active ? '' : 'Inactive', path: `/banners/edit/${b.promo_banner_id}` })),
  },
  {
    key: 'quotes', label: 'Quotes', icon: 'quotes', route: '/quotes',
    records: (s, q) => asArray(s.quotes?.all_quotes)
      .filter((it) => inc(it.quote_text, q) || inc(it.quote_speaker_name, q))
      .map((it) => ({ label: it.quote_text || 'Untitled', meta: it.quote_speaker_name || '', path: `/quotes/edit/${it.quote_id}` })),
  },
  {
    key: 'card-decks', label: 'Card Decks', icon: 'card-decks', route: '/card-decks',
    records: (s, q) => asArray(s.card_decks?.decks)
      .filter((d) => inc(d.name, q))
      .map((d) => ({ label: d.name || 'Untitled deck', meta: d.is_active ? `${d.active_card_count ?? 0} cards` : 'Inactive', path: `/card-decks/${d.id}/cards` })),
  },
  {
    key: 'marketing-initiatives', label: 'Marketing Initiatives', icon: 'share', route: '/marketing-initiatives',
    records: (s, q) => asArray(s.initiatives?.initiatives)
      .filter((i) => inc(i.name, q) || inc(i.code, q))
      .map((i) => ({ label: i.name || i.code || 'Untitled', meta: i.code, path: `/marketing-initiatives/${i.id}/edit` })),
  },
  {
    // Media resources are modal-based with no per-item URL, so a result links
    // to the list page (best we can do without deep-linking the modal).
    key: 'media-resources', label: 'Media Resources', icon: 'featured', route: '/media-resources',
    records: (s, q) => asArray(s.mediaResources?.mediaResources)
      .filter((r) => inc(r.name, q) || inc(r.description, q))
      .map((r) => ({ label: r.name || r.file_name || 'Untitled', meta: r.description || '', path: '/media-resources' })),
  },
];

/** Resolve the source key for the current pathname (longest list-route prefix). */
export function sectionKeyForPath(pathname) {
  const match = CONTENT_SOURCES
    .filter((s) => pathname === s.route || pathname.startsWith(`${s.route}/`))
    .sort((a, b) => b.route.length - a.route.length)[0];
  return match?.key || null;
}

/**
 * Run every source for `query` against `state`, returning a flat list of
 * records each tagged with its group (key/label/icon).
 *
 * Every match is returned — nothing is capped or de-duplicated here. How many
 * of them are on screen at once is the palette's call (it renders
 * INITIAL_PER_GROUP per section and reveals the rest on "Show more"), which
 * keeps "what matched" and "what is currently visible" from being conflated.
 *
 * Async-source aborts propagate (the palette swallows them); other per-source
 * errors are isolated so one bad source can't blank the whole result set.
 */
export async function runGlobalSearch(query, state, signal) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];

  const perSource = await Promise.all(CONTENT_SOURCES.map(async (src) => {
    try {
      const items = src.async ? await src.fetch(q, signal) : src.records(state, q);
      return (items || []).map((r) => ({
        ...r,
        icon: src.icon,
        group: src.label,
        groupKey: src.key,
      }));
    } catch (e) {
      if (e?.name === 'AbortError' || e?.name === 'CanceledError') throw e;
      // eslint-disable-next-line no-console
      console.warn(`[globalSearch] source "${src.key}" failed:`, e);
      return [];
    }
  }));

  return perSource.flat();
}

/* ─── lazy loading ────────────────────────────────────────────────────── */

// [reducer slice, data key, fetch action] for the sync (Redux-backed) sources.
const SYNC_FETCHERS = [
  ['categories', 'all_categories', fetchAllCategories],
  ['subcategories', 'subcategories', fetchAllSubCategories],
  ['authors', 'authors', fetchAuthors],
  ['challenges', 'challenges', fetchAllChallenges],
  ['series', 'all_series', fetchAllSeries],
  ['daily_reflections', 'daily_reflections', fetchAllDailyReflections],
  ['background_musics', 'background_musics', fetchAllBGM],
  ['tapping_points', 'tapping_points', fetchTappingPoints],
  ['banners', 'banners', fetchAllPromoBanners],
  ['quotes', 'all_quotes', fetchAllQuotes],
  ['card_decks', 'decks', fetchAllDecks],
  ['initiatives', 'initiatives', fetchAllMarketingInitiatives],
  ['mediaResources', 'mediaResources', fetchAllMediaResources],
];

const isEmpty = (data) => !data || (Array.isArray(data)
  ? data.length === 0
  : Object.keys(data).length === 0);

/** Best-effort: warm any empty content slice so global search is complete. */
export function ensureContentLoaded(state, dispatch) {
  SYNC_FETCHERS.forEach(([slice, key, action]) => {
    if (isEmpty(state?.[slice]?.[key])) {
      try { dispatch(action()); } catch (e) { /* best-effort */ }
    }
  });
}
